import { Select, SelectOptionType } from '@recruit-robin/robin-theme/web-components';
import { ChevronDown, ChevronUp } from '@recruit-robin/robin-theme/web-icons';
import { memo, useEffect, useMemo, useRef, useState, type FocusEvent } from 'react';
import { useDebounce } from 'react-use';

import { searchClient } from '@/clients/SearchClient/SearchClient';
import type { LocationResponse } from '@/clients/SearchClient/SearchClient.types';
import { useGetAddressQuery, type AddressViewModel } from '@/shared/lib/address';
import FormHelperError from '@/shared/legacy/components/FormHelperError';

export type SingleLocationSelectProps = {
  addressValue: string;
  onAddressChange: ({
    address,
    city,
    latitude,
    longitude,
    place_id,
  }: {
    address: string;
    city: string;
    latitude: number | null;
    longitude: number | null;
    place_id: string | null;
  }) => void;
  onBlur?: () => void;
  placeholder: string;
  label?: string;
  error?: string;
};

const emptyAddressPayload = {
  address: '',
  city: '',
  latitude: null,
  longitude: null,
  place_id: null,
};

export const SingleLocationSelect = memo(
  ({ addressValue, onAddressChange, onBlur, placeholder, error }: SingleLocationSelectProps) => {
    const cachedDetails = useRef<Record<string, LocationResponse>>({});
    const containerRef = useRef<HTMLDivElement>(null);
    const [search, setSearch] = useState('');
    const [searchPrefixDebounced, setSearchPrefixDebounced] = useState('');
    const errorId = useRef(`single-location-select-error-${Math.random().toString(36).slice(2)}`);

    useEffect(() => {
      setSearch(String(addressValue ?? '').trim());
    }, [addressValue]);

    useDebounce(() => setSearchPrefixDebounced(search.trim()), 250, [search]);

    const { data: addresses } = useGetAddressQuery({ search: searchPrefixDebounced, mode: 'geocode' });

    const addressByPlaceId = useMemo(() => {
      const map = new Map<string, AddressViewModel>();
      for (const option of addresses ?? []) {
        map.set(option.placeId, option);
      }
      return map;
    }, [addresses]);

    const dropdownSelectOptions = useMemo((): SelectOptionType<string>[] => {
      return (addresses ?? []).map(({ address, placeId }) => ({
        value: placeId,
        label: address,
      }));
    }, [addresses]);

    const selectedOption = useMemo((): SelectOptionType<string> | undefined => {
      const trimmed = String(addressValue ?? '').trim();
      if (!trimmed) {
        return undefined;
      }
      const match = (addresses ?? []).find((o) => o.address === trimmed);
      if (match) {
        return { value: match.placeId, label: match.address };
      }
      return { value: trimmed, label: trimmed };
    }, [addressValue, addresses]);

    const filteredDropdownOptions = useMemo(() => {
      const q = search.trim().toLowerCase();
      if (!q) {
        return dropdownSelectOptions;
      }
      return dropdownSelectOptions.filter((o) => o.label.toLowerCase().includes(q));
    }, [dropdownSelectOptions, search]);

    const resolveLocationDetails = async (placeId: string): Promise<LocationResponse | null> => {
      if (cachedDetails.current[placeId]) {
        return cachedDetails.current[placeId];
      }
      try {
        const { data } = await searchClient.getLocationDetails(placeId);
        cachedDetails.current[placeId] = data;
        return data;
      } catch {
        return null;
      }
    };

    const handleSelectChange = async (opt: SelectOptionType<string> | undefined) => {
      if (!opt) {
        onAddressChange(emptyAddressPayload);
        return;
      }
      const matched = addressByPlaceId.get(opt.value);
      if (!matched) {
        onAddressChange({
          address: opt.value,
          city: '',
          latitude: null,
          longitude: null,
          place_id: null,
        });
        return;
      }
      const details = await resolveLocationDetails(matched.placeId);
      if (!details) {
        onAddressChange({
          address: matched.address,
          city: '',
          latitude: null,
          longitude: null,
          place_id: matched.placeId,
        });
        return;
      }
      const city = details.raw?.city?.long_name ?? details.raw?.city?.short_name ?? '';
      onAddressChange({
        address: matched.address,
        city,
        latitude: details.lat,
        longitude: details.lon,
        place_id: matched.placeId,
      });
    };

    const handleContainerBlur = (e: FocusEvent<HTMLDivElement>) => {
      if (containerRef.current?.contains(e.relatedTarget as Node | null)) {
        return;
      }
      onBlur?.();
    };

    const handleTextChange = (text: string) => {
      setSearch(text);
      const currentAddress = String(addressValue ?? '').trim();
      if (text.trim() === currentAddress) {
        return;
      }
      onAddressChange({
        address: text,
        city: '',
        latitude: null,
        longitude: null,
        place_id: null,
      });
    };

    return (
      <div ref={containerRef} onBlur={handleContainerBlur}>
        <Select
          value={selectedOption}
          options={dropdownSelectOptions}
          renderOptions={filteredDropdownOptions}
          onChange={handleSelectChange}
          closeOnClickOutside
          autocomplete
          placeholder={placeholder}
          text={search}
          onTextChange={handleTextChange}
          endIcon={({ visible }) => (
            <div onClick={(e) => e.stopPropagation()}>
              {visible ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          )}
        />
        {error ? <FormHelperError id={errorId.current} message={error} /> : null}
      </div>
    );
  },
);
